import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Icon } from 'semantic-ui-react';
import { useToasts } from 'common/toast';
import { useCreatePlansCultureActionMutation, useUpdatePlansCultureActionMutation } from '@/areas/culture-hub/api/plansCultureActions';
import { INVOLVED_OPTIONS, renderInvolvedIcon } from '@/areas/culture-hub/components/interventions/culture-actions/involvedHelpers';
import { Textarea } from '@/common/ui/textarea';
import { StyledButton } from '@/common/components/button/StyledButton';
import { MobilePageContent, MobilePageSection, Title, FieldLabel } from '@/mobile/layouts/common/MobilePageStyles';

interface MobileCultureActionEditorProps {
  onClose: () => void;
  actionPlanId?: number | null;
  initial?: any;
}

const defaultModel = {
  action_taken: '',
  involved_party: undefined as number | undefined,
  observations: '',
  next_actions: '',
  impact: undefined as number | undefined,
};

const IMPACT_LEVELS = [
  { value: 0, label: 'None', color: '#DCFCE7' },
  { value: 1, label: 'Low', color: '#86EFAC' },
  { value: 2, label: 'Medium', color: '#4ADE80' },
  { value: 3, label: 'High', color: '#16A34A' },
  { value: 4, label: 'Huge', color: '#09815f' },
];

const maxImpact = IMPACT_LEVELS.length - 1;
const getRangeBackground = (val: number) => {
  const percent = (val / maxImpact) * 100;
  const color = IMPACT_LEVELS[val]?.color ?? IMPACT_LEVELS[1].color;
  return `linear-gradient(90deg, ${color} ${percent}%, #e5e7eb ${percent}%)`;
};

const MobileCultureActionEditor: React.FC<MobileCultureActionEditorProps> = ({ onClose, actionPlanId, initial }) => {
  const [form, setForm] = useState<any>({ ...defaultModel });
  const [create] = useCreatePlansCultureActionMutation();
  const [update] = useUpdatePlansCultureActionMutation();
  const { addToast } = useToasts();

  useEffect(() => {
    if (initial) {
      setForm({
        action_taken: initial.action_taken ?? '',
        involved_party: initial.involved_party ?? undefined,
        observations: initial.observations ?? '',
        next_actions: initial.next_actions ?? '',
        impact: initial.impact ?? undefined,
      });
    } else {
      setForm({ ...defaultModel });
    }
  }, [initial, onClose]);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleClickOutside = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setDropdownOpen(false);
    }
  };

  React.useEffect(() => {
    if (!dropdownOpen) return;
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  const handleChange = (field: string, value: any) => setForm((prev: any) => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    try {
      if (!actionPlanId && !initial) {
        addToast('No action plan selected. Cannot save culture action.', { appearance: 'error', autoDismiss: true });
        return;
      }

      if (initial && initial.id) {
        const payload = {
          ...form,
          assessment_period_action_plan: initial.assessment_period_action_plan ?? actionPlanId ?? undefined,
        };

        await update({
          id: initial.id,
          assessmentPeriodActionPlanCultureActionId: initial.id,
          assessmentPeriodActionPlanCultureAction: payload,
        }).unwrap();
        addToast('Culture action updated', { appearance: 'success', autoDismiss: true });
      } else {
        await create({
          assessmentPeriodActionPlanCultureAction: {
            ...form,
            assessment_period_action_plan: actionPlanId,
          },
        }).unwrap();
        addToast('Culture action added', { appearance: 'success', autoDismiss: true });
      }

      onClose();
    } catch (err: any) {
      console.error('Failed to save culture action:', err);
      addToast('Failed to save culture action', { appearance: 'error', autoDismiss: true });
    }
  };

  return (
    <MobilePageContent>
      <MobilePageSection padbottom="20px">
        <Title>{initial ? 'Edit Culture Action' : 'Log Culture Action'}</Title>
      </MobilePageSection>

      <MobilePageSection>
        <FieldGroup>
          <FieldLabel>Action Taken</FieldLabel>
          <Textarea rows={3} value={form.action_taken} onChange={(e: any) => handleChange('action_taken', e.target.value)} />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>People Involved</FieldLabel>
          <SelectDropdown ref={dropdownRef}>
            <SelectButton type="button" onClick={() => setDropdownOpen((open) => !open)}>
              <SelectedIcon>{renderInvolvedIcon(form.involved_party, 16)}</SelectedIcon>
              <SelectedLabel>{INVOLVED_OPTIONS.find((option) => option.value === form.involved_party)?.label || 'Choose involved party'}</SelectedLabel>
              <Icon name={dropdownOpen ? 'angle up' : 'angle down'} />
            </SelectButton>
            {dropdownOpen && (
              <OptionsMenu role="listbox">
                {INVOLVED_OPTIONS.map((opt) => (
                  <OptionItem
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={form.involved_party === opt.value}
                    onClick={() => {
                      handleChange('involved_party', opt.value);
                      setDropdownOpen(false);
                    }}
                  >
                    <IconWrapper>{renderInvolvedIcon(opt.value, 16)}</IconWrapper>
                    <div>{opt.label}</div>
                  </OptionItem>
                ))}
              </OptionsMenu>
            )}
          </SelectDropdown>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Observations</FieldLabel>
          <Textarea rows={3} value={form.observations} onChange={(e: any) => handleChange('observations', e.target.value)} />
        </FieldGroup>

        <FieldGroup>
          <LabelRow>
            <FieldLabel>Impact</FieldLabel>
          </LabelRow>
          <ImpactInput
            type="range"
            min={0}
            max={maxImpact}
            step={1}
            value={form.impact ?? 0}
            onChange={(e: any) => handleChange('impact', Number(e.target.value))}
            style={{ background: getRangeBackground(form.impact ?? 0) }}
          />
          <ImpactOptions>
            {IMPACT_LEVELS.map((item) => (
              <ImpactOption key={item.value} type="button" onClick={() => handleChange('impact', item.value)}>
                {item.label}
              </ImpactOption>
            ))}
          </ImpactOptions>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>Next Actions</FieldLabel>
          <Textarea rows={3} value={form.next_actions} onChange={(e: any) => handleChange('next_actions', e.target.value)} />
        </FieldGroup>

        <ActionsRow>
          <StyledButton label="Cancel" color="gray" width="100%" height="42px" onClick={onClose} />
          <StyledButton label="Save" color="blue" width="100%" height="42px" onClick={handleSave} />
        </ActionsRow>
      </MobilePageSection>
    </MobilePageContent>
  );
};

export default MobileCultureActionEditor;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const OptionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
`;

const IconWrapper = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eff6ff;
`;

const SelectDropdown = styled.div`
  position: relative;
`;

const SelectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  font-size: 14px;
  text-align: left;
`;

const SelectedIcon = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #eff6ff;
`;

const SelectedLabel = styled.div`
  flex: 1;
  min-width: 0;
  color: #111827;
  font-size: 14px;
  text-align: left;
`;

const OptionsMenu = styled.div`
  position: absolute;
  z-index: 120;
  width: 100%;
  top: calc(100% + 8px);
  left: 0;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 14px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
  padding: 8px 0;
`;

const OptionItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  background: transparent;
  border: none;
  padding: 10px 14px;
  cursor: pointer;
  text-align: left;
  color: #111827;

  &:hover,
  &[aria-selected='true'] {
    background: #f8fafc;
  }
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

const ImpactInput = styled.input`
  width: 100%;
  height: 12px;
  border-radius: 999px;
  appearance: none;
  outline: none;
  margin-top: 6px;
  margin-bottom: 8px;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    background: #2563eb;
    border: 2px solid #ffffff;
    cursor: pointer;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 9999px;
    background: #2563eb;
    border: 2px solid #ffffff;
    cursor: pointer;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
  }
`;

const ImpactOptions = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
`;

const ImpactOption = styled.button`
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 11px;
  cursor: pointer;
  padding: 0;
`;

const ActionsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 16px;
`;
